<template>
  <lb-drawer
    :Maxwidth="drawerMaxWidth"
    :value="visible"
    @input="$emit('update:visible', $event)"
    heading="Bulk Upload"
    :loading="loading"
    v-if="visible"
    data-testid="tasklist__bulk-checklist-upload-drawer"
  >
    <template v-slot:body>
      <div>
        <div class="d-flex align-center justify-space-between">
          <span class="text-center mb-1" v-if="uploadData.length === 0">Upload your document</span>
          <span v-else>Preview</span>
          <div class="d-flex">
            <v-btn
              text
              class="secondaryBtn"
              v-if="uploadData.length > 0"
              @click="$emit('reupload')"
              data-testid="tasklist__bulk-upload-reupload-btn"
            >
              <v-icon>mdi-reload</v-icon> <span>Re-Upload</span>
            </v-btn>
            <v-btn
              data-testid="tasklist__bulk-work-paper-download-template-btn"
              class="secondaryBtn d-flex align-center mb-1"
              @click="$emit('download-template')"
              ><span>Download Template</span></v-btn
            >
          </div>
        </div>
        <div class="d-flex align-center justify-space-between" v-if="uploadData.length > 0">
          <v-checkbox
            label="Show only items with error"
            color="caccounting"
            :input-value="showDataWithError"
            @change="$emit('update:showDataWithError', $event)"
            data-testid="tasklist__bulk-work-paper-show-error-checkbox"
          />
          <v-spacer />
        </div>
      </div>
      <div class="d-flex align-center justify-center">
        <DragDropfile
          v-if="uploadData.length === 0"
          hidedetails
          :files="file"
          @update:files="$emit('update:file', $event)"
          :resetTrigger="resetTrigger"
          data-testid="tasklist__bulk-work-paper-drag-drop-file"
        />
      </div>
      <div
        v-if="uploadData.length > 0"
        class="radius-small border-on simple-table"
        style="max-height: 73vh; overflow: scroll"
        data-testid="tasklist__bulk-upload-preview-table"
      >
        <v-simple-table data-testid="tasklist__bulk-upload-preview-table">
          <template v-slot:default>
            <thead class="tableHeaderBg">
              <th class="text-center tableHeaderBg" scope="sno">S No.</th>
              <th class="text-center tableHeaderBg" scope="task_type">Task type</th>
              <th class="text-center tableHeaderBg" scope="description">Task Name</th>
              <th class="text-center tableHeaderBg" scope="category">Category</th>
              <th class="text-center tableHeaderBg" scope="subcategory">Sub Category</th>
              <th class="text-center tableHeaderBg" scope="location">Location</th>
              <th class="text-center tableHeaderBg" scope="assertions">Assertions</th>
              <th class="text-center tableHeaderBg" scope="purpose">Purpose</th>
              <th class="text-center tableHeaderBg" scope="instructionHe">Instruction</th>
              <th class="text-center tableHeaderBg" scope="preparer">Preparer</th>
              <th class="text-center tableHeaderBg" scope="approver">Approver</th>
              <th class="text-center tableHeaderBg" scope="tags">Frequency</th>
              <th class="text-center tableHeaderBg" scope="duedays">Due days</th>
              <th class="text-center tableHeaderBg" scope="glcode">Account No.</th>
              <th class="text-center tableHeaderBg" scope="priority">Priority</th>
              <th class="text-center tableHeaderBg" scope="evidences">No of Evidences</th>
              <th
                v-for="(customColumn, index) in customColumns"
                :key="index"
                class="text-center tableHeaderBg"
                scope="customColumn"
              >
                {{
                  customColumn.dataType === 'date'
                    ? `${customColumn.displayText} (dd/mm/yyyy)`
                    : customColumn.displayText
                }}
              </th>
            </thead>
            <v-slide-y-transition group tag="tbody" style="overflow: hidden !important">
              <tr v-for="(row, index) in filteredData" :key="index">
                <td>{{ index + 1 }}</td>
                <template v-for="(cell, key) in row">
                  <td
                    v-if="errorsonly[index] && errorsonly[index][key]"
                    :key="`error-${key}`"
                    class="font-size-xm pa-0 border-right-data"
                  >
                    <div class="all-width all-height" style="border: 1px solid #f3b9ba">
                      <v-tooltip bottom content-class="tooltip-bottom error-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-if="key === 'assertions' || key === 'location'"
                            class="d-inline-block"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-chip
                              v-for="(error, errorKey) in cell"
                              :key="errorKey"
                              label
                              color="error"
                              data-testid="tasklist__error-chip"
                              outlined
                              class="pl-2 mb-1 mr-1 error-chip"
                            >
                              <span class="pl-1">{{ error }}</span>
                            </v-chip>
                          </div>

                          <span class="d-flex align-center error-text" v-else v-bind="attrs" v-on="on" data-testid="tasklist__error-text">
                            <v-icon color="error-text" class="mr-1">mdi-information</v-icon>
                            <span class="error-text">{{ errorsonly[index][key] }}</span>
                          </span>
                        </template>
                        <span>{{ errorsonly[index][key] }}</span>
                      </v-tooltip>
                    </div>
                  </td>
                  <td v-else :key="`value-${key}`">
                    <OverflowList
                      v-if="key === 'tags' || key === 'location'"
                      :items="cell || []"
                      data-testid="tasklist__overflow-badge"
                    >
                      <template #primary="{ item }">
                        <v-chip
                          label
                          data-testid="tasklist__tag-chip"
                          outlined
                          class="lightpurple caccounting--text pl-2 mb-1 mr-1"
                        >
                          <span class="pl-1">{{ item }}</span>
                        </v-chip>
                      </template>
                    </OverflowList>
                    <div v-else-if="key === 'approver' || key === 'preparer'">
                      <v-avatar
                        v-for="(va, ka) in cell"
                        :key="ka"
                        size="24"
                        outlined
                        class="lightpurple caccounting--text mb-1 mr-1"
                        data-testid="tasklist__user-avatar"
                      >
                        <v-tooltip top content-class="tooltip-top">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                              {{ (va || '')[0] }}
                            </span>
                          </template>
                          <span>{{ va }}</span>
                        </v-tooltip>
                      </v-avatar>
                    </div>
                    <OverflowList
                      v-else-if="key === 'assertions'"
                      :items="cell || []"
                      data-testid="tasklist__assertions-overflow-badge"
                    />
                    <div v-else-if="key === 'glcode'">
                      <div v-for="(va, ka) in cell" :key="ka" outlined class="pl-2 mb-1 mr-1">
                        <span class="pl-1">{{ va }}</span>
                      </div>
                    </div>
                    <div v-else-if="key === 'priority'">
                      {{ cell }}
                    </div>
                    <div v-else class="two-line-clamp">{{ cell }}</div>
                  </td>
                </template>
              </tr>
            </v-slide-y-transition>
          </template>
        </v-simple-table>
      </div>
      <p v-if="checkListErrorResponse" class="error--text ma-0 pa-0 mt-4 font-size-xm">
        *{{ checkListErrorResponse }}
      </p>
      <p v-if="hasValidationErrors" class="error--text ma-0 pa-0 mt-4 font-size-xm">
        *These are fields where errors exist / information missing. Please rectify and re-upload to continue. (Preparer
        & Approver cannot be the same user)
      </p>
    </template>
    <template v-slot:actions>
      <v-spacer />
      <div v-if="uploadData.length > 0">
        <v-btn
          data-testid="tasklist__bulk-upload-submit-btn"
          class="ml-2 white--text"
          color="primary"
          v-if="!hasValidationErrors"
          @click="$emit('upload')"
          ><span>Upload</span></v-btn
        >
        <v-btn class="" @click="$emit('cancel')" data-testid="tasklist__bulk-upload-cancel-btn"><span>Cancel</span></v-btn>
      </div>
    </template>
  </lb-drawer>
</template>

<script>
import { OverflowList } from '@/modules/financialclose/components';
import DragDropfile from '../../../../../../../views/Inputs/DragDropfile.vue';
import { drawerWidths } from '../../../../../../../styles/design-tokens.js';

export default {
  name: 'BulkChecklistUploadDrawer',
  components: {
    DragDropfile,
    OverflowList,
  },
  computed: {
    drawerMaxWidth() {
      return this.uploadData.length > 0 ? drawerWidths.expanded : drawerWidths.lg;
    },
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    uploadData: {
      type: Array,
      default: () => [],
    },
    file: {
      type: [Array, Object, null],
      default: null,
    },
    resetTrigger: {
      type: Boolean,
      default: false,
    },
    showDataWithError: {
      type: Boolean,
      default: false,
    },
    filteredData: {
      type: Array,
      default: () => [],
    },
    errorsonly: {
      type: Array,
      default: () => [],
    },
    customColumns: {
      type: Array,
      default: () => [],
    },
    checkListErrorResponse: {
      type: String,
      default: '',
    },
    hasValidationErrors: {
      type: Boolean,
      default: false,
    },
  },
};
</script>
